import React, { useState, useCallback } from "react";
import { RefreshCw, BarChart } from "lucide-react";
import type { UserWithLikes } from "../types";
import "@/styles/UserAnalytics.css";
import { TabNavigation } from "@/components/User_Analytics/TabNavigation";
import {
  UserLikesDetail,
  UserLikesList,
} from "@/components/User_Analytics/UserLikes";
import { MoreFeatures } from "@/components/User_Analytics/MoreFeatures";
import { useUserAnalytics } from "@/hooks/useUserAnalytics";

/**
 * UserAnalyticsPage - Main analytics dashboard with tabbed interface
 * Following SOLID principles with modular component architecture
 * Updated: Fixed import issues
 */

export const UserAnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("user-likes");
  const {
    usersWithLikes,
    selectedUserLikes,
    selectedUserId,
    isLoading,
    error,
    fetchUserLikes,
    setSelectedUserId,
    refreshData,
  } = useUserAnalytics();

  const tabs = [
    {
      id: "user-likes",
      label: "User Likes",
    },

  ];

  const handleUserSelect = useCallback(
    (userId: string) => {
      fetchUserLikes(userId);
    },
    [fetchUserLikes]
  );

  const handleBackToUsers = useCallback(() => {
    setSelectedUserId(null);
  }, [setSelectedUserId]);

  const getSelectedUser = useCallback((): UserWithLikes | null => {
    if (!selectedUserId) return null;
    return (
      usersWithLikes.find((user: any) => user.user_id === selectedUserId) ||
      null
    );
  }, [selectedUserId, usersWithLikes]);

  const renderUserLikesContent = () => {
    if (selectedUserId) {
      return (
        <UserLikesDetail
          user={getSelectedUser()}
          likes={selectedUserLikes}
          onBack={handleBackToUsers}
          isLoading={isLoading}
        />
      );
    }

    return (
      <UserLikesList
        users={usersWithLikes}
        onUserSelect={handleUserSelect}
        isLoading={isLoading}
      />
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "user-likes":
        return renderUserLikesContent();
      case "more-features":
        return <MoreFeatures />;
      default:
        return renderUserLikesContent();
    }
  };

  return (
    <div className="user-analytics-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <BarChart size={32} />
          </div>
          <div className="header-text">
            <h1>User Analytics</h1>
            <p>Insights into user behavior and engagement patterns</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            onClick={refreshData}
            className="refresh-button"
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "spinning" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={refreshData} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="analytics-content">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          className="analytics-tabs"
        />

        <div className="tab-content">{renderTabContent()}</div>
      </div>
    </div>
  );
};
